/**
 * i.MX USB Loader Service
 * TypeScript port of imx_loader.js with improved error handling and testability
 *
 * This is a 1:1 port of the NXP imx_usb_loader functionality using WebHID
 * Based on imx_usb.c, imx_sdp.c, and related source files
 */

import {
  DeviceConfig,
  DeviceConfigChain,
  DeviceStatus,
  FileType,
  HABSecurityMode,
  HIDReportID,
  IHIDDevice,
  ILogger,
  IVTHeader,
  BootData,
  MkimageHeader,
  ProcessResult,
  ProgressCallback,
  ReportWaiter,
  SDPCommand,
  WorkItem,
} from '../types/imx-types';

/**
 * IMX Loader Service
 * Handles all protocol-level communication with i.MX devices via WebHID
 */
export class IMXLoaderService {
  private device: IHIDDevice | null = null;
  private currentConfig: DeviceConfig | null = null;
  private currentConfigChain: DeviceConfigChain | null = null;

  // Report buffering for cross-platform compatibility
  private reportBuffer: Map<number, Uint8Array[]> = new Map();
  private reportWaiters: Map<number, ReportWaiter[]> = new Map();
  private globalInputHandler: ((event: any) => void) | null = null;

  // EID for boot script injection
  private eid: string | null = null;

  // CRC32 lookup table (polynomial 0xEDB88320)
  private readonly CRC32_TABLE: Uint32Array;

  // Protocol constants
  private readonly IVT_HEADER_TAG = 0xd1;
  private readonly IVT_VERSION = 0x40;
  private readonly IVT_VERSION_IMX8M = 0x41;
  private readonly DCD_HEADER_TAG = 0xd2;
  private readonly DCD_VERSION = 0x40;
  private readonly HAB_MAX_DCD_SIZE = 1768;

  // i.MX SDP response status codes (from NXP i.MX reference manual)
  private readonly DCD_LOAD_SUCCESS = 0x128a8a12;
  private readonly FILE_LOAD_SUCCESS = 0x88888888;

  // DCD sub-command tags (HAB protocol)
  private readonly DCD_WRITE_DATA_CMD = 0xcc000004;
  private readonly DCD_CHECK_DATA_CMD = 0xcf000004;

  // U-Boot mkimage format magic
  private readonly MKIMAGE_MAGIC = 0x27051956;

  constructor(
    private logger: ILogger,
    private configs: Record<string, DeviceConfig>,
    private deviceConfigs: DeviceConfigChain[],
    private onProgress?: ProgressCallback
  ) {
    // Initialize CRC32 lookup table
    this.CRC32_TABLE = new Uint32Array(256);
    for (let i = 0; i < 256; i++) {
      let crc = i;
      for (let j = 0; j < 8; j++) {
        crc = crc & 1 ? 0xedb88320 ^ (crc >>> 1) : crc >>> 1;
      }
      this.CRC32_TABLE[i] = crc;
    }
  }

  // ========================================================================
  // Public API
  // ========================================================================

  /**
   * Connect to a device
   */
  async connectDevice(device: IHIDDevice): Promise<DeviceConfig> {
    this.logger.info('Opening device connection...');

    // Only open if not already open
    if (!device.opened) {
      await device.open();
    } else {
      this.logger.info('Device already open, skipping open()');
    }
    this.device = device;

    // Setup input report buffering
    this.clearReportBuffer();
    this.setupInputReportHandler();

    // Find matching configuration
    const { config, configChain } = await this.findMatchingConfig(device);
    this.currentConfig = config;
    this.currentConfigChain = configChain;

    this.logger.success(
      `Connected to ${device.productName || 'Unknown Device'} ` +
        `(VID:0x${device.vendorId.toString(16).padStart(4, '0')}, ` +
        `PID:0x${device.productId.toString(16).padStart(4, '0')})`
    );

    // Get initial device status
    await this.doStatus();

    return config;
  }

  /**
   * Disconnect from device
   */
  async disconnect(): Promise<void> {
    if (this.device) {
      if (this.globalInputHandler) {
        this.device.removeEventListener('inputreport', this.globalInputHandler);
        this.globalInputHandler = null;
      }

      this.clearReportBuffer();

      await this.device.close();
      this.device = null;
      this.currentConfig = null;

      this.logger.info('Device disconnected');
    }
  }

  /**
   * Load images for current device stage
   */
  async loadImages(fileLoader: (path: string) => Promise<Uint8Array>): Promise<boolean> {
    if (!this.device || !this.currentConfig) {
      throw new Error('No device connected');
    }

    this.logger.info('Starting multi-stage boot sequence...');

    const workItems = this.currentConfig.work;
    if (!workItems || workItems.length === 0) {
      throw new Error('No work items defined in configuration');
    }

    this.logger.info(`Processing ${workItems.length} file(s) for ${this.currentConfig.name}`);
    this.logger.info(`Config: ${JSON.stringify(this.currentConfig, null, 2)}`);

    // Process each work item
    for (let i = 0; i < workItems.length; i++) {
      const work = workItems[i];
      const isLastItem = i === workItems.length - 1;

      this.logger.info(`\n=== Work item ${i + 1}/${workItems.length}: ${work.filename} ===`);
      this.logger.info(`Work config: loadAddr=0x${work.loadAddr?.toString(16) || 'N/A'}, jump=${work.jump}, jumpAddr=0x${work.jumpAddr?.toString(16) || 'N/A'}, dcd=${work.dcd}, isLastItem=${isLastItem}`);

      // Load the file
      const fileData = await fileLoader(work.filename);

      // Calculate simple checksum for debugging
      const checksum = fileData.reduce((sum, byte) => (sum + byte) & 0xFFFFFFFF, 0);
      this.logger.info(`File loaded: ${fileData.length} bytes, checksum=0x${checksum.toString(16)}`);

      // Process based on file type
      if (work.filename.endsWith('.imx')) {
        await this.processIMXFile(fileData, work, isLastItem);
      } else {
        await this.processRawFile(fileData, work, isLastItem);
      }
    }

    this.logger.success('All files loaded for this stage!');

    // Check if this was a jump that would cause re-enumeration
    const lastWork = workItems[workItems.length - 1];
    if (lastWork.jump) {
      this.logger.info('Device is rebooting and will re-enumerate with new VID:PID');

      // Give device a moment to start rebooting
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Disconnect
      await this.disconnect();
      this.logger.success('Ready to connect to next stage!');
    }

    return true;
  }

  /**
   * Get current device
   */
  getDevice(): IHIDDevice | null {
    return this.device;
  }

  /**
   * Get current configuration
   */
  getConfig(): DeviceConfig | null {
    return this.currentConfig;
  }

  /**
   * Set the EID to inject into boot script
   * @param eid - The Equipment ID (serial number) to inject
   */
  setEid(eid: string): void {
    this.eid = eid;
    this.logger.info(`EID set for injection: ${eid}`);
  }

  /**
   * Get the current EID
   */
  getEid(): string | null {
    return this.eid;
  }

  // ========================================================================
  // Protocol Commands
  // ========================================================================

  /**
   * Query device status and HAB security state
   */
  private async doStatus(): Promise<DeviceStatus> {
    this.logger.info('Getting device status...');

    const cmd = this.buildStatusCommand();
    await this.doCommand(cmd);

    // Read HAB security state (Report 3)
    const habSecurity = await this.doResponse(HIDReportID.SECURITY);
    if (habSecurity === null) {
      throw new Error('Failed to get HAB security state');
    }

    const mode =
      habSecurity === HABSecurityMode.PRODUCTION ? 'production' : 'development';
    this.logger.success(`HAB security state: ${mode} (0x${habSecurity.toString(16).padStart(8, '0')})`);

    // Read status (Report 4) - only in HID mode
    try {
      const status = await this.doResponse(HIDReportID.DATA_IN, true);
      if (status !== null) {
        this.logger.debug(`Status: 0x${status.toString(16).padStart(8, '0')}`);
      }
    } catch (err) {
      // Status read may fail, which is OK
      this.logger.debug('Status read skipped (expected)');
    }

    return {
      habSecurity,
      habSecurityString: mode,
    };
  }

  /**
   * Read memory from device
   */
  private async readMemory(addr: number, count: number): Promise<Uint8Array> {
    this.logger.debug(`Reading memory: addr=0x${addr.toString(16)}, count=${count}`);

    const cmd = this.buildReadRegCommand(addr, count);
    await this.doCommand(cmd);

    await this.doResponse(HIDReportID.SECURITY);

    // Read data in chunks
    const result = new Uint8Array(count);
    let offset = 0;
    let remaining = count;
    let retries = 0;

    while (remaining > 0) {
      try {
        const chunkSize = Math.min(remaining, 64);
        const chunk = await this.transferHID(HIDReportID.DATA_IN, null, chunkSize);

        const copySize = Math.min(chunk.length, remaining);
        result.set(chunk.slice(0, copySize), offset);
        offset += copySize;
        remaining -= copySize;
        retries = 0;
      } catch (err) {
        retries++;
        if (retries > 8) {
          throw new Error(`Failed to read memory after ${retries} retries`);
        }
        this.logger.warning(`Read retry ${retries}/8`);
      }
    }

    this.logger.debug(`Read memory result: ${this.toHex(result)}`);
    return result;
  }

  /**
   * Write value to memory
   */
  private async writeMemory(addr: number, value: number): Promise<void> {
    this.logger.debug(`Writing memory: addr=0x${addr.toString(16)}, value=0x${value.toString(16)}`);

    const cmd = this.buildWriteRegCommand(addr, value);
    await this.doCommand(cmd);

    await this.doResponse(HIDReportID.SECURITY);
    await this.doResponse(HIDReportID.DATA_IN);
  }

  /**
   * Write DCD table using bulk WRITE_DCD command
   */
  private async writeDCD(dcdData: Uint8Array): Promise<number> {
    const length = this.fromBE16(dcdData, 1); // header.length is at offset 1-2

    if (length > this.HAB_MAX_DCD_SIZE) {
      throw new Error(`DCD too large: ${length} bytes`);
    }

    this.logger.info(`Loading DCD table @0x${this.currentConfig!.dcdAddr.toString(16)}`);

    const cmd = this.buildDLDcdCommand(this.currentConfig!.dcdAddr, length);
    await this.doCommand(cmd);

    const transferSize = await this.doDataTransfer(dcdData.slice(0, length));

    this.logger.info(`DCD transfer: ${length} bytes, ${transferSize} transferred`);

    // Get response (must read SECURITY report before DATA_IN per SDP protocol)
    await this.doResponse(HIDReportID.SECURITY);
    const status = await this.doResponse(HIDReportID.DATA_IN);

    if (status === this.DCD_LOAD_SUCCESS) {
      this.logger.success('DCD load succeeded');
    } else {
      this.logger.warning(`DCD load status: 0x${status?.toString(16)}`);
    }

    return transferSize;
  }

  /**
   * Process DCD table entry by entry (fallback method)
   */
  private async writeDCDTable(dcdData: Uint8Array): Promise<void> {
    const length = this.fromBE16(dcdData, 1);

    this.logger.info(`Processing DCD table: ${length} bytes`);

    let offset = 4; // Skip DCD header
    const end = length;

    while (offset < end) {
      const subTag = dcdData[offset];
      const subLength = this.fromBE16(dcdData, offset + 1);
      const param = dcdData[offset + 3];
      const flags = param & 0xf8;
      const subTagFull = (subTag << 24) | (param & 0x7);

      this.logger.debug(`DCD sub-command: tag=0x${subTagFull.toString(16)}, length=${subLength}`);

      if (subTagFull === this.DCD_WRITE_DATA_CMD) {
        // Write Data Command
        if (flags & 0xe8) {
          throw new Error(`Write Data Command with unsupported flags: 0x${flags.toString(16)}`);
        }

        let dataOffset = offset + 4;
        const subEnd = offset + subLength;

        while (dataOffset < subEnd) {
          const addr = this.fromBE32(dcdData, dataOffset);
          const val = this.fromBE32(dcdData, dataOffset + 4);
          this.logger.debug(`Write data *0x${addr.toString(16)} = 0x${val.toString(16)}`);
          await this.writeMemory(addr, val);
          dataOffset += 8;
        }
      } else if (subTagFull === this.DCD_CHECK_DATA_CMD) {
        // Check Data Command
        const addr = this.fromBE32(dcdData, offset + 4);
        const mask = this.fromBE32(dcdData, offset + 8);
        let count = 10000;

        if (subLength === 16) {
          count = this.fromBE32(dcdData, offset + 12);
        }

        this.logger.debug(`Check Data Command: addr=0x${addr.toString(16)}, mask=0x${mask.toString(16)}, count=${count}`);

        // Poll memory location
        let success = false;
        for (let i = 0; i < count; i++) {
          const valBytes = await this.readMemory(addr, 4);
          const val = this.fromLE32(valBytes, 0);

          if (flags === 0x00 && (val & mask) === 0) {
            success = true;
            break;
          } else if (flags === 0x08 && (val & mask) === mask) {
            success = true;
            break;
          } else if (flags === 0x10 && (val & mask) === mask) {
            success = true;
            break;
          } else if (flags === 0x18 && (val & mask) !== 0) {
            success = true;
            break;
          }
        }

        if (success) {
          this.logger.success('Check Data Command succeeded');
        } else {
          this.logger.warning('Check Data Command expired without condition met');
        }
      } else {
        throw new Error(`Unknown DCD sub-command: 0x${subTagFull.toString(16)}`);
      }

      offset += subLength;
    }
  }

  /**
   * Load file to device memory
   */
  private async loadFile(
    fileData: Uint8Array,
    dladdr: number,
    fsize: number,
    type: FileType
  ): Promise<number> {
    this.logger.info(`Loading file to 0x${dladdr.toString(16)}, size=${fsize}, type=0x${type.toString(16)}`);

    const cmd = this.buildWriteFileCommand(dladdr, fsize, type);
    await this.doCommand(cmd);

    const transferSize = await this.doDataTransfer(fileData.slice(0, fsize));

    this.logger.info(`File transfer: ${fsize} bytes, ${transferSize} transferred`);

    // Get response (must read SECURITY report before DATA_IN per SDP protocol)
    await this.doResponse(HIDReportID.SECURITY);
    const status = await this.doResponse(HIDReportID.DATA_IN);

    if (status === this.FILE_LOAD_SUCCESS) {
      this.logger.success('File load succeeded');
    } else {
      this.logger.warning(`File load status: 0x${status?.toString(16)}`);
    }

    return transferSize;
  }

  /**
   * Jump to execute code at address
   */
  private async jump(headerAddr: number): Promise<void> {
    this.logger.info(`Jumping to 0x${headerAddr.toString(16)}`);

    const cmd = this.buildJumpCommand(headerAddr);
    await this.doCommand(cmd);

    const security = await this.doResponse(HIDReportID.SECURITY);

    try {
      const status = await this.doResponse(HIDReportID.DATA_IN, true);
      // If we get a response, the jump command returned an error
      // However, in some cases the device may reboot anyway
      this.logger.warning(
        `Jump command returned status: 0x${status?.toString(16)} (security: 0x${security?.toString(16)})`
      );
      this.logger.info('Device may still reboot - waiting for re-enumeration...');
    } catch (err) {
      // If Report 4 fails/times out, the jump succeeded cleanly
      this.logger.success('Jump succeeded (device executing code)');
    }
  }

  // ========================================================================
  // File Processing
  // ========================================================================

  /**
   * Process IMX file with IVT/DCD
   */
  private async processIMXFile(
    fileData: Uint8Array,
    work: WorkItem,
    shouldJump: boolean
  ): Promise<ProcessResult> {
    this.logger.info(`Processing .imx file: ${work.filename}`);

    // Find IVT header (may be at offset 0, 0x400, or 0x8400)
    const offsets = [0, 0x400, 0x8400];
    let ivt: IVTHeader | null = null;
    let headerOffset = 0;

    for (const offset of offsets) {
      if (offset + 32 <= fileData.length) {
        ivt = this.parseIVTHeader(fileData.slice(offset));
        if (ivt) {
          headerOffset = offset;
          this.logger.info(`Found IVT header at offset 0x${headerOffset.toString(16)}`);
          break;
        }
      }
    }

    if (!ivt) {
      throw new Error('No valid IVT header found in file');
    }

    // Adjust file data to start at header
    const adjustedData = fileData.slice(headerOffset);
    const bootData = this.getBootData(adjustedData, ivt);

    if (!bootData) {
      throw new Error('No boot data found');
    }

    // Get DCD table if requested by work config
    const dcd = work.dcd ? this.getDCD(adjustedData, ivt) : null;

    // Process DCD if present
    if (dcd) {
      try {
        // Try WRITE_DCD command first (faster)
        await this.writeDCD(dcd);
      } catch (err) {
        this.logger.warning(`WRITE_DCD failed: ${(err as Error).message}`);
        this.logger.info('Falling back to direct DCD processing...');
        await this.writeDCDTable(dcd);
      }
    }

    // Determine load address and size
    const headerAddr = ivt.selfPtr;
    const fileBase = headerAddr - headerOffset;
    let dladdr = bootData.dest;
    let maxLength = bootData.imageLen;

    if (fileBase > dladdr) {
      // Adjust for the offset between file_base and dest
      const diff = fileBase - dladdr;
      this.logger.info(
        `Adjusting: fileBase (0x${fileBase.toString(16)}) > dest (0x${dladdr.toString(16)}), diff=${diff} bytes`
      );
      maxLength -= diff;
      dladdr = fileBase;
    }

    // Use the minimum of actual file size and max_length
    const actualFileSize = adjustedData.length;
    const transferSize = Math.min(actualFileSize, maxLength);

    this.logger.info(`Transfer: ${transferSize} bytes to 0x${dladdr.toString(16)} (file=${actualFileSize}, max=${maxLength})`);

    const type = shouldJump && work.jump ? FileType.APP : FileType.LOAD_ONLY;

    // Prepare data to send
    const dataToSend = new Uint8Array(transferSize);
    dataToSend.set(adjustedData.slice(0, transferSize));

    // CRITICAL: When using "jump header" mode, clear the DCD pointer
    if (work.jump === 'header' && dcd) {
      this.logger.info('Clearing DCD pointer in IVT header (required for jump header mode)');
      // dcd_ptr is at offset 0x0C in the IVT header (little endian)
      dataToSend[0x0c] = 0;
      dataToSend[0x0d] = 0;
      dataToSend[0x0e] = 0;
      dataToSend[0x0f] = 0;
    }

    await this.loadFile(dataToSend, dladdr, transferSize, type);

    // Jump to execute only if this is the last file and jump is requested
    if (shouldJump && work.jump) {
      // Determine jump address based on work config
      let jumpAddr: number;
      if (work.jumpAddr) {
        jumpAddr = work.jumpAddr;
      } else if (work.jump === 'header') {
        // Jump to header (selfPtr) for ROM mode
        jumpAddr = ivt.selfPtr;
      } else {
        // Default to start address
        jumpAddr = ivt.startAddr;
      }

      this.logger.info(`Jumping to 0x${jumpAddr.toString(16)}`);
      await this.jump(jumpAddr);
    } else {
      this.logger.info('Skipping jump (not last file or jump not requested)');
    }

    return {
      success: true,
      ivt,
      bootData,
      dcdProcessed: dcd !== null,
      bytesLoaded: transferSize,
      jumped: shouldJump && !!work.jump,
    };
  }

  /**
   * Process raw file (no IVT)
   */
  private async processRawFile(
    fileData: Uint8Array,
    work: WorkItem,
    shouldJump: boolean
  ): Promise<void> {
    let processedData = fileData;

    // Inject EID into boot-sdp.scr if EID is set
    if (work.filename === 'boot-sdp.scr' && this.eid) {
      this.logger.info(`Injecting EID ${this.eid} into boot script`);
      processedData = this.modifyBootScriptWithEid(fileData, this.eid);
    }

    this.logger.info(`Processing raw file: ${processedData.length} bytes`);
    this.logger.info(`  shouldJump=${shouldJump}, work.jump=${work.jump}, work.jumpAddr=0x${work.jumpAddr?.toString(16) || 'N/A'}`);

    if (!work.loadAddr) {
      throw new Error('Load address not specified for raw file');
    }

    const loadAddr = work.loadAddr;
    const fileSize = processedData.length;
    const type = shouldJump && work.jump ? FileType.APP : FileType.LOAD_ONLY;

    this.logger.info(`Loading to 0x${loadAddr.toString(16)}, size=${fileSize}, type=0x${type.toString(16)} (${type === FileType.APP ? 'APP' : 'LOAD_ONLY'})`);

    const cmd = this.buildWriteFileCommand(loadAddr, fileSize, type);
    this.logger.info(`WRITE_FILE cmd: ${Array.from(cmd).map(b => b.toString(16).padStart(2, '0')).join(' ')}`);
    await this.doCommand(cmd);

    await this.doDataTransfer(processedData);

    // Get response
    const security = await this.doResponse(HIDReportID.SECURITY);
    const status = await this.doResponse(HIDReportID.DATA_IN);

    this.logger.info(`Response: security=0x${security?.toString(16)}, status=0x${status?.toString(16)}`);

    if (status === this.FILE_LOAD_SUCCESS) {
      this.logger.success(`File loaded successfully to 0x${loadAddr.toString(16)}`);
    } else {
      this.logger.warning(`File load status: 0x${status?.toString(16)} (expected 0x${this.FILE_LOAD_SUCCESS.toString(16)})`);
    }

    // Jump if this is the last file and jump is requested
    const willJump = shouldJump && work.jump && work.jumpAddr;
    this.logger.info(`Jump decision: shouldJump=${shouldJump} && work.jump=${work.jump} && work.jumpAddr=${work.jumpAddr} => willJump=${willJump}`);

    if (willJump) {
      this.logger.info(`>>> EXECUTING JUMP to 0x${work.jumpAddr!.toString(16)}`);
      await this.jump(work.jumpAddr!);
      this.logger.info(`<<< JUMP COMPLETE`);
    } else {
      this.logger.info(`Skipping jump (not requested or not last item)`);
    }
  }

  // ========================================================================
  // IVT/DCD Parsing
  // ========================================================================

  /**
   * Parse IVT header from file data
   */
  private parseIVTHeader(fileData: Uint8Array): IVTHeader | null {
    if (fileData.length < 32) {
      return null;
    }

    const tag = fileData[0];
    const version = fileData[3];

    if (tag !== this.IVT_HEADER_TAG) {
      return null; // Not an IVT header
    }

    if (version !== this.IVT_VERSION && version !== this.IVT_VERSION_IMX8M) {
      this.logger.warning(`Unknown IVT version: 0x${version.toString(16)}`);
    }

    const ivt: IVTHeader = {
      tag,
      length: this.fromBE16(fileData, 1),
      version,
      startAddr: this.fromLE32(fileData, 4),
      reserved1: this.fromLE32(fileData, 8),
      dcdPtr: this.fromLE32(fileData, 12),
      bootDataPtr: this.fromLE32(fileData, 16),
      selfPtr: this.fromLE32(fileData, 20),
      csfPtr: this.fromLE32(fileData, 24),
      reserved2: this.fromLE32(fileData, 28),
    };

    this.logger.info(`IVT Header found: version=0x${ivt.version.toString(16)}, selfPtr=0x${ivt.selfPtr.toString(16)}`);
    this.logger.debug(`  startAddr=0x${ivt.startAddr.toString(16)}`);
    this.logger.debug(`  dcdPtr=0x${ivt.dcdPtr.toString(16)}`);
    this.logger.debug(`  bootDataPtr=0x${ivt.bootDataPtr.toString(16)}`);

    return ivt;
  }

  /**
   * Get boot data from IVT
   */
  private getBootData(fileData: Uint8Array, ivt: IVTHeader): BootData | null {
    if (!ivt.bootDataPtr || ivt.bootDataPtr === 0) {
      return null;
    }

    const offset = ivt.bootDataPtr - ivt.selfPtr;

    if (offset < 0 || offset + 12 > fileData.length) {
      throw new Error(`Invalid boot_data_ptr: 0x${ivt.bootDataPtr.toString(16)}`);
    }

    const bootData: BootData = {
      dest: this.fromLE32(fileData, offset),
      imageLen: this.fromLE32(fileData, offset + 4),
      plugin: this.fromLE32(fileData, offset + 8),
      offset,
    };

    this.logger.debug(
      `Boot Data: dest=0x${bootData.dest.toString(16)}, len=${bootData.imageLen}, plugin=${bootData.plugin}`
    );

    return bootData;
  }

  /**
   * Get DCD table from IVT
   */
  private getDCD(fileData: Uint8Array, ivt: IVTHeader): Uint8Array | null {
    if (!ivt.dcdPtr || ivt.dcdPtr === 0) {
      this.logger.info('No DCD table');
      return null;
    }

    const offset = ivt.dcdPtr - ivt.selfPtr;

    if (offset < 0 || offset + 4 > fileData.length) {
      throw new Error(`Invalid dcd_ptr: 0x${ivt.dcdPtr.toString(16)}`);
    }

    const dcdTag = fileData[offset];
    const dcdVersion = fileData[offset + 3];
    const dcdLength = this.fromBE16(fileData, offset + 1);

    if (dcdTag !== this.DCD_HEADER_TAG) {
      throw new Error(`Invalid DCD header tag: 0x${dcdTag.toString(16)}`);
    }

    if (dcdVersion !== this.DCD_VERSION) {
      this.logger.warning(`Unknown DCD version: 0x${dcdVersion.toString(16)}`);
    }

    if (dcdLength === 0) {
      this.logger.info('DCD length is 0, skipping');
      return null;
    }

    if (offset + dcdLength > fileData.length) {
      throw new Error(`DCD extends beyond file: ${dcdLength} bytes`);
    }

    this.logger.info(`DCD found: length=${dcdLength} bytes`);

    return fileData.slice(offset, offset + dcdLength);
  }

  // ========================================================================
  // Configuration Management
  // ========================================================================

  /**
   * Find matching configuration for device
   */
  private async findMatchingConfig(
    device: IHIDDevice
  ): Promise<{ config: DeviceConfig; configChain: DeviceConfigChain }> {
    const vid = device.vendorId;
    const pid = device.productId;

    this.logger.info(`Searching for device config: VID=0x${vid.toString(16).padStart(4, '0')}, PID=0x${pid.toString(16).padStart(4, '0')}`);

    // If we already have a config chain from a previous stage, search within that chain first
    if (this.currentConfigChain) {
      this.logger.info('Searching within current config chain for next stage...');
      for (const [confVid, confPid, confFile] of this.currentConfigChain) {
        if (confVid === vid && confPid === pid) {
          this.logger.success(`Matched config in current chain: ${confFile}`);
          const config = this.configs[confFile];
          if (!config) {
            throw new Error(`Config file '${confFile}' not found in configs!`);
          }
          return { config, configChain: this.currentConfigChain };
        }
      }
      this.logger.warning('Device not found in current config chain, searching all chains...');
    }

    // Search all config chains
    for (const configChain of this.deviceConfigs) {
      for (const [confVid, confPid, confFile] of configChain) {
        if (confVid === vid && confPid === pid) {
          this.logger.success(`Matched config: ${confFile}`);
          const config = this.configs[confFile];
          if (!config) {
            throw new Error(`Config file '${confFile}' not found in configs!`);
          }
          return { config, configChain };
        }
      }
    }

    throw new Error(`No configuration found for device VID:PID ${vid.toString(16)}:${pid.toString(16)}`);
  }

  // ========================================================================
  // Low-level HID Transfer
  // ========================================================================

  /**
   * Setup global input report handler for buffering
   */
  private setupInputReportHandler(): void {
    if (this.globalInputHandler || !this.device) {
      return;
    }

    this.globalInputHandler = (event: any) => {
      const { reportId, data: reportData } = event;
      const bytes = new Uint8Array(reportData.buffer);

      this.logger.debug(`Received report ${reportId}, size: ${bytes.length}`);
      if (bytes.length > 0) {
        this.logger.debug(`Data: ${this.toHex(bytes)}`);
      }

      // Check if anyone is waiting for this report
      if (this.reportWaiters.has(reportId) && this.reportWaiters.get(reportId)!.length > 0) {
        // Deliver to the first waiter
        const waiter = this.reportWaiters.get(reportId)!.shift()!;
        clearTimeout(waiter.timeout);
        this.logger.debug(`Delivering report ${reportId} to waiting promise`);
        waiter.resolve(bytes);
      } else {
        // Buffer the report for later
        if (!this.reportBuffer.has(reportId)) {
          this.reportBuffer.set(reportId, []);
        }
        this.reportBuffer.get(reportId)!.push(bytes);
        this.logger.debug(
          `Buffered report ${reportId} (buffer has ${this.reportBuffer.get(reportId)!.length} reports)`
        );
      }
    };

    this.device.addEventListener('inputreport', this.globalInputHandler);
  }

  /**
   * Clear report buffer and waiters
   */
  private clearReportBuffer(): void {
    // Cancel all waiting promises
    for (const [reportId, waiters] of this.reportWaiters) {
      for (const waiter of waiters) {
        clearTimeout(waiter.timeout);
        waiter.reject(new Error(`Report buffer cleared while waiting for report ${reportId}`));
      }
    }
    this.reportBuffer.clear();
    this.reportWaiters.clear();
  }

  /**
   * Transfer HID report
   */
  private async transferHID(
    reportId: number,
    data: Uint8Array | null,
    expectedSize = 0
  ): Promise<Uint8Array> {
    if (!this.device) {
      throw new Error('No device connected');
    }

    try {
      if (reportId === HIDReportID.CMD || reportId === HIDReportID.DATA_OUT) {
        // Send data (Reports 1 and 2)
        await this.device.sendReport(reportId, data! as BufferSource);
        return data!;
      } else {
        // Receive data (Reports 3 and 4)
        this.logger.debug(`Waiting for report ${reportId}`);

        // Check if report is already buffered
        if (this.reportBuffer.has(reportId) && this.reportBuffer.get(reportId)!.length > 0) {
          const buffered = this.reportBuffer.get(reportId)!.shift()!;
          this.logger.debug(
            `Retrieved report ${reportId} from buffer (${this.reportBuffer.get(reportId)!.length} remain)`
          );
          return buffered;
        }

        // Register a waiter for this report
        return new Promise<Uint8Array>((resolve, reject) => {
          const timeout = setTimeout(() => {
            // Remove this waiter from the queue
            if (this.reportWaiters.has(reportId)) {
              const waiters = this.reportWaiters.get(reportId)!;
              const index = waiters.findIndex((w) => w.resolve === resolve);
              if (index >= 0) {
                waiters.splice(index, 1);
              }
            }
            reject(new Error(`Timeout waiting for report ${reportId}`));
          }, 5000);

          // Add to waiters queue
          if (!this.reportWaiters.has(reportId)) {
            this.reportWaiters.set(reportId, []);
          }
          this.reportWaiters.get(reportId)!.push({ resolve, reject, timeout });
          this.logger.debug(
            `Registered waiter for report ${reportId} (${this.reportWaiters.get(reportId)!.length} waiting)`
          );
        });
      }
    } catch (err) {
      this.logger.error(`Transfer error (report ${reportId}): ${(err as Error).message}`);
      throw err;
    }
  }

  /**
   * Send command with retries
   */
  private async doCommand(cmd: Uint8Array, retries = 5): Promise<void> {
    this.logger.debug(`Sending command: ${this.toHex(cmd, 4)}`);

    while (retries > 0) {
      try {
        await this.transferHID(HIDReportID.CMD, cmd);
        return;
      } catch (err) {
        retries--;
        if (retries === 0) {
          throw err;
        }
        this.logger.warning(`Command failed, retrying... (${retries} left)`);
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    }
  }

  /**
   * Read response
   */
  private async doResponse(reportId: number, silent = false): Promise<number | null> {
    try {
      const response = await this.transferHID(reportId, null);

      if (response.length < 4) {
        if (!silent) {
          this.logger.error(`Response too short: ${response.length} bytes`);
        }
        return null;
      }

      // Most responses are big-endian 32-bit values
      const value = this.fromBE32(response, 0);

      if (!silent) {
        this.logger.debug(`Report ${reportId} response: 0x${value.toString(16).padStart(8, '0')}`);
      }

      return value;
    } catch (err) {
      if (!silent) {
        this.logger.error(`Response error (report ${reportId}): ${(err as Error).message}`);
      }
      throw err;
    }
  }

  /**
   * Transfer data in chunks
   */
  private async doDataTransfer(data: Uint8Array): Promise<number> {
    const len = data.length;
    let offset = 0;
    let retries = 10;
    let maxChunk = this.currentConfig?.maxTransfer || 1024;
    let transferSize = 0;

    this.logger.debug(`Starting data transfer: ${len} bytes`);

    while (offset < len) {
      const remaining = len - offset;
      const chunkSize = Math.min(remaining, maxChunk);

      try {
        const chunk = data.slice(offset, offset + chunkSize);
        await this.transferHID(HIDReportID.DATA_OUT, chunk);

        transferSize += chunkSize;
        offset += chunkSize;
        retries = 10;
        maxChunk = this.currentConfig?.maxTransfer || 1024;

        // Update progress (less frequently to reduce overhead)
        if (offset % (maxChunk * 10) === 0 || offset === len) {
          const percent = (offset / len) * 100;
          if (this.onProgress) {
            this.onProgress(percent, `Transferring: ${Math.round(percent)}%`);
          }
        }

        // Small delay every 64KB to allow device to process
        if (offset % 65536 === 0) {
          await new Promise((resolve) => setTimeout(resolve, 5));
        }
      } catch (err) {
        this.logger.warning(`Transfer error: ${(err as Error).message}, retrying...`);

        if (maxChunk >= 16) {
          maxChunk = (maxChunk >> 1) & ~0x03; // Divide by 2, align to 4
        } else {
          maxChunk <<= 1;
        }

        await new Promise((resolve) => setTimeout(resolve, 10));
        retries--;

        if (retries === 0) {
          throw new Error('Data transfer failed after all retries');
        }
      }
    }

    this.logger.success(`Data transfer complete: ${transferSize} bytes`);
    return transferSize;
  }

  // ========================================================================
  // Command Builders
  // ========================================================================

  private buildReadRegCommand(addr: number, cnt: number): Uint8Array {
    const cmd = new Uint8Array(16);
    const view = new DataView(cmd.buffer);

    view.setUint16(0, SDPCommand.READ_REG, false);
    view.setUint32(2, addr, false);
    cmd[6] = 0x20; // format
    view.setUint32(7, cnt, false);
    view.setUint32(11, 0, false); // data
    cmd[15] = 0x00; // reserved

    return cmd;
  }

  private buildWriteRegCommand(addr: number, value: number): Uint8Array {
    const cmd = new Uint8Array(16);
    const view = new DataView(cmd.buffer);

    view.setUint16(0, SDPCommand.WRITE_REG, false);
    view.setUint32(2, addr, false);
    cmd[6] = 0x20; // format
    view.setUint32(7, 4, false); // count = 4 bytes
    view.setUint32(11, value, false);
    cmd[15] = 0x00;

    return cmd;
  }

  private buildStatusCommand(): Uint8Array {
    const cmd = new Uint8Array(16);
    const view = new DataView(cmd.buffer);

    view.setUint16(0, SDPCommand.ERROR_STATUS, false);
    // All other fields are 0

    return cmd;
  }

  private buildDLDcdCommand(dcdAddr: number, length: number): Uint8Array {
    const cmd = new Uint8Array(16);
    const view = new DataView(cmd.buffer);

    view.setUint16(0, SDPCommand.WRITE_DCD, false);
    view.setUint32(2, dcdAddr, false);
    cmd[6] = 0x00;
    view.setUint32(7, length, false);
    view.setUint32(11, 0, false);
    cmd[15] = 0x00;

    return cmd;
  }

  private buildWriteFileCommand(dladdr: number, fsize: number, type: FileType): Uint8Array {
    const cmd = new Uint8Array(16);
    const view = new DataView(cmd.buffer);

    view.setUint16(0, SDPCommand.WRITE_FILE, false);
    view.setUint32(2, dladdr, false);
    cmd[6] = 0x00;
    view.setUint32(7, fsize, false);
    view.setUint32(11, 0, false);
    cmd[15] = type;

    return cmd;
  }

  private buildJumpCommand(headerAddr: number): Uint8Array {
    const cmd = new Uint8Array(16);
    const view = new DataView(cmd.buffer);

    view.setUint16(0, SDPCommand.JUMP_ADDRESS, false);
    view.setUint32(2, headerAddr, false);
    cmd[6] = 0x00;
    view.setUint32(7, 0, false);
    view.setUint32(11, 0, false);
    cmd[15] = 0x00;

    return cmd;
  }

  // ========================================================================
  // Boot Script Modification (EID Injection)
  // ========================================================================

  /**
   * Calculate CRC32 checksum for data
   */
  private calculateCRC32(data: Uint8Array): number {
    let crc = 0xffffffff;
    for (let i = 0; i < data.length; i++) {
      crc = this.CRC32_TABLE[(crc ^ data[i]) & 0xff] ^ (crc >>> 8);
    }
    return (crc ^ 0xffffffff) >>> 0;
  }

  /**
   * Parse mkimage header from boot script file
   */
  private parseMkimageHeader(fileData: Uint8Array): MkimageHeader {
    if (fileData.length < 64) {
      throw new Error(
        `File too small for mkimage header: ${fileData.length} bytes (expected at least 64)`
      );
    }

    const view = new DataView(fileData.buffer, fileData.byteOffset);

    const header: MkimageHeader = {
      magic: view.getUint32(0, false), // big-endian
      headerCRC: view.getUint32(4, false),
      timestamp: view.getUint32(8, false),
      dataSize: view.getUint32(12, false),
      loadAddr: view.getUint32(16, false),
      entryPoint: view.getUint32(20, false),
      dataCRC: view.getUint32(24, false),
      os: fileData[28],
      arch: fileData[29],
      imageType: fileData[30],
      compression: fileData[31],
      name: new TextDecoder()
        .decode(fileData.slice(32, 64))
        .replace(/\0.*$/, ''),
    };

    // Validate magic number
    if (header.magic !== this.MKIMAGE_MAGIC) {
      throw new Error(
        `Invalid mkimage magic: 0x${header.magic.toString(16)} (expected 0x${this.MKIMAGE_MAGIC.toString(16)})`
      );
    }

    return header;
  }

  /**
   * Create a new mkimage header with updated data size and CRC
   */
  private createMkimageHeader(
    originalHeader: MkimageHeader,
    newDataSize: number,
    newDataCRC: number
  ): Uint8Array {
    const header = new Uint8Array(64);
    const view = new DataView(header.buffer);

    // Write header fields (all big-endian)
    view.setUint32(0, this.MKIMAGE_MAGIC, false); // Magic
    view.setUint32(4, 0, false); // Header CRC (calculated later)
    view.setUint32(8, Math.floor(Date.now() / 1000), false); // Timestamp
    view.setUint32(12, newDataSize, false); // New data size
    view.setUint32(16, originalHeader.loadAddr, false);
    view.setUint32(20, originalHeader.entryPoint, false);
    view.setUint32(24, newDataCRC, false); // New data CRC
    header[28] = originalHeader.os;
    header[29] = originalHeader.arch;
    header[30] = originalHeader.imageType;
    header[31] = originalHeader.compression;

    // Copy name (max 32 bytes, null-terminated)
    const nameBytes = new TextEncoder().encode(originalHeader.name);
    header.set(nameBytes.slice(0, 32), 32);

    // Calculate header CRC (with CRC field set to 0)
    const headerCRC = this.calculateCRC32(header);
    view.setUint32(4, headerCRC, false);

    return header;
  }

  /**
   * Extract script text from boot script data (after mkimage header)
   */
  private extractScriptText(scriptData: Uint8Array, header: MkimageHeader): string {
    // Script content starts after 64-byte mkimage header
    const content = scriptData.slice(64, 64 + header.dataSize);

    if (content.length < 8) {
      throw new Error(
        `Script data too short: ${content.length} bytes (expected at least 8)`
      );
    }

    // U-Boot scripts have an 8-byte header:
    // Bytes 0-3: Script text length (32-bit big-endian)
    // Bytes 4-7: Padding (zeros)
    const scriptTextBytes = content.slice(8);
    return new TextDecoder().decode(scriptTextBytes);
  }

  /**
   * Modify boot script to include EID in bootargs
   *
   * @param scriptData - Original boot script file data (mkimage format)
   * @param eid - Equipment ID (serial number) to inject
   * @returns Modified boot script file data
   * @throws Error if modification fails
   */
  private modifyBootScriptWithEid(scriptData: Uint8Array, eid: string): Uint8Array {
    // Validate EID format (should be digits only)
    if (!/^\d+$/.test(eid)) {
      throw new Error(
        `Invalid EID format: "${eid}" (expected digits only)`
      );
    }

    // Parse the mkimage header
    const header = this.parseMkimageHeader(scriptData);

    // Extract the script text
    let scriptText = this.extractScriptText(scriptData, header);

    // Find and modify the bootargs line
    // Pattern matches: setenv bootargs <anything until newline>
    const bootargsRegex = /(setenv bootargs [^\n]+)/;
    const match = scriptText.match(bootargsRegex);

    if (!match) {
      throw new Error('Could not find "setenv bootargs" in boot script');
    }

    // Append serial parameter to bootargs
    const originalBootargs = match[1];
    const modifiedBootargs = `${originalBootargs} serial=${eid}`;
    scriptText = scriptText.replace(bootargsRegex, modifiedBootargs);

    this.logger.info(`Modified bootargs: added serial=${eid}`);
    this.logger.debug(`Original: ${originalBootargs}`);
    this.logger.debug(`Modified: ${modifiedBootargs}`);

    // Convert modified script back to bytes
    const newScriptTextBytes = new TextEncoder().encode(scriptText);

    // Create new data section with 8-byte header + script text
    const newContent = new Uint8Array(8 + newScriptTextBytes.length);
    const newContentView = new DataView(newContent.buffer);

    // Write 8-byte script header
    newContentView.setUint32(0, newScriptTextBytes.length, false); // Script length (big-endian)
    newContentView.setUint32(4, 0, false); // Padding (zeros)

    // Write script text
    newContent.set(newScriptTextBytes, 8);

    // Calculate new data CRC
    const newDataCRC = this.calculateCRC32(newContent);

    // Create updated mkimage header
    const newHeader = this.createMkimageHeader(header, newContent.length, newDataCRC);

    // Combine header + content
    const result = new Uint8Array(newHeader.length + newContent.length);
    result.set(newHeader, 0);
    result.set(newContent, newHeader.length);

    this.logger.info(`Boot script modified: ${scriptData.length} -> ${result.length} bytes`);

    return result;
  }

  // ========================================================================
  // Utility Methods
  // ========================================================================

  private fromBE32(bytes: Uint8Array, offset = 0): number {
    const view = new DataView(bytes.buffer, bytes.byteOffset + offset, 4);
    return view.getUint32(0, false);
  }

  private fromBE16(bytes: Uint8Array, offset = 0): number {
    const view = new DataView(bytes.buffer, bytes.byteOffset + offset, 2);
    return view.getUint16(0, false);
  }

  private fromLE32(bytes: Uint8Array, offset = 0): number {
    const view = new DataView(bytes.buffer, bytes.byteOffset + offset, 4);
    return view.getUint32(0, true);
  }

  private toHex(bytes: Uint8Array, maxLen = 16): string {
    const arr = Array.from(bytes.slice(0, maxLen));
    return arr.map((b) => b.toString(16).padStart(2, '0')).join(' ');
  }
}
